// Sugalarm — web-dashboard "Patterns & insights" panel (cycle 178).
//
// WEB FRAME ONLY (mounted only by the web-gated DashboardScreen). Adds the
// plain-language layer on top of the cycle-170 shell + cycle-177 AGP:
//   • a 24-hour TIME-OF-DAY distribution of alarm episodes — highs ABOVE a
//     baseline, lows BELOW it, with the overnight window emphasized,
//   • 2–4 deterministic, strictly DESCRIPTIVE insights,
//   • a facts strip (best / toughest day, in-target streak), and
//   • a CGM COVERAGE read that visibly flags reduced reliability.
//
// Everything is computed CLIENT-SIDE from data the dashboard already loads
// (alarm rows, daily summaries, the window's readings) via the pure
// `lib/patterns` module — no new endpoint, no LLM. The cycle-172
// observational disclaimer already covers this panel; nothing here repeats
// it. All fills are CSS tokens, so it re-tints in light/dark.

import { useMemo } from "react";

import type { Alert, DailySummary, Reading } from "../../lib/types";
import {
  bucketEpisodes,
  buildInsights,
  computeCoverage,
  factsFromDaily,
  modalWindow,
  periodLabelForHours,
  COVERAGE_LOW_PCT,
  STREAK_TIR_PCT,
} from "../../lib/patterns";
import styles from "./PatternsPanel.module.css";

const VIEW_W = 960;
const H = 196;
const PAD_L = 28;
const PAD_R = 12;
const PAD_T = 16;
const PAD_B = 22;

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/** Format a "YYYY-MM-DD" day key without timezone drift (no Date parse). */
function fmtDayKey(key: string): string {
  const [y, m, d] = key.split("-").map((n) => parseInt(n, 10));
  if (!y || !m || !d) return key;
  return `${MONTHS[m - 1] ?? m} ${d}`;
}

interface Props {
  /** Alarm rows spanning ~2× the window (current + prior period). */
  alertsWide: Alert[];
  /** The current window's readings (for coverage). */
  readings: Reading[];
  /** Recent daily summaries, most-recent-first. */
  daily: DailySummary[];
  hours: number;
  endMs: number;
}

export function PatternsPanel({ alertsWide, readings, daily, hours, endMs }: Props) {
  const windowMs = hours * 60 * 60 * 1000;
  const startMs = endMs - windowMs;
  const priorStartMs = endMs - 2 * windowMs;
  const periodLabel = periodLabelForHours(hours);

  const model = useMemo(() => {
    const buckets = bucketEpisodes(alertsWide, startMs, endMs);
    const prior = bucketEpisodes(alertsWide, priorStartMs, startMs - 1);
    const priorTotal = prior.lowsTotal + prior.highsTotal;
    const coverage = computeCoverage(readings, windowMs);
    const facts = factsFromDaily(daily);
    const insights = buildInsights({
      buckets,
      // Only compare against the prior period when it actually had alarms —
      // a zero prior can't be told apart from "no data there", so we omit
      // the trend rather than imply a change that may be a coverage gap.
      priorAlarmTotal: priorTotal > 0 ? priorTotal : null,
      periodLabel,
    });
    return { buckets, coverage, facts, insights };
  }, [alertsWide, readings, daily, startMs, endMs, priorStartMs, windowMs, periodLabel]);

  const { buckets, coverage, facts, insights } = model;

  // ── Distribution geometry ───────────────────────────────────────────
  const innerW = VIEW_W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;
  const baselineY = PAD_T + innerH / 2;
  const halfH = innerH / 2 - 4;
  const colW = innerW / 24;
  const barW = colW * 0.6;
  const maxCount = Math.max(
    1,
    ...buckets.lowsByHour,
    ...buckets.highsByHour,
  );

  const lowModal = modalWindow(buckets.lowsByHour);

  const xLabels = [0, 6, 12, 18, 24].map((h) => ({
    h,
    x: PAD_L + h * colW,
    label: h === 0 || h === 24 ? "12a" : h === 12 ? "12p" : h < 12 ? `${h}a` : `${h - 12}p`,
  }));

  const hasEpisodes = buckets.lowsTotal + buckets.highsTotal > 0;

  return (
    <section className={styles.panel}>
      <div className={styles.head}>
        <span className={styles.cardLabel}>Patterns &amp; insights</span>
        <span className={styles.hint}>
          alarm episodes by time of day · observational
        </span>
      </div>

      {/* Reliability caveat — only when coverage is low. */}
      {coverage.low ? (
        <div className={styles.caveat} role="note">
          <svg className={styles.caveatIcon} viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M12 3.5 21 19H3L12 3.5Z"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinejoin="round"
            />
            <path d="M12 10v4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            <circle cx="12" cy="16.6" r="0.9" fill="currentColor" />
          </svg>
          <span>
            CGM coverage was only {coverage.pct}% in this {periodLabel} — these
            patterns may be unreliable.
          </span>
        </div>
      ) : null}

      {/* 24h distribution: highs up, lows down, overnight emphasized. */}
      <div className={styles.chartWrap}>
        <svg
          className={styles.svg}
          height={H}
          viewBox={`0 0 ${VIEW_W} ${H}`}
          preserveAspectRatio="none"
          role="img"
          aria-label="Alarm episodes by hour of day: highs above the baseline, lows below"
        >
          {/* Overnight band [00:00, 06:00). */}
          <rect
            x={PAD_L}
            y={PAD_T}
            width={6 * colW}
            height={innerH}
            fill="var(--pattern-overnight)"
          />
          {/* Baseline. */}
          <line
            x1={PAD_L}
            y1={baselineY}
            x2={PAD_L + innerW}
            y2={baselineY}
            stroke="var(--ink-4)"
            strokeWidth={1}
          />
          {/* Per-hour bars. */}
          {Array.from({ length: 24 }, (_, h) => {
            const cx = PAD_L + (h + 0.5) * colW;
            const hi = buckets.highsByHour[h]!;
            const lo = buckets.lowsByHour[h]!;
            const hiH = (hi / maxCount) * halfH;
            const loH = (lo / maxCount) * halfH;
            return (
              <g key={h}>
                {hi > 0 ? (
                  <rect
                    x={cx - barW / 2}
                    y={baselineY - hiH}
                    width={barW}
                    height={hiH}
                    rx={1.5}
                    fill="var(--zone-warn)"
                  />
                ) : null}
                {lo > 0 ? (
                  <rect
                    x={cx - barW / 2}
                    y={baselineY}
                    width={barW}
                    height={loH}
                    rx={1.5}
                    fill="var(--zone-urgent)"
                  />
                ) : null}
              </g>
            );
          })}
        </svg>
        <div className={styles.axisLayer} aria-hidden="true">
          {xLabels.map((l) => (
            <span
              key={l.h}
              className={styles.xLabel}
              style={{
                left: `${(l.x / VIEW_W) * 100}%`,
                transform:
                  l.h === 0
                    ? "translate(0,0)"
                    : l.h === 24
                    ? "translate(-100%,0)"
                    : "translate(-50%,0)",
              }}
            >
              {l.label}
            </span>
          ))}
        </div>
        {!hasEpisodes ? (
          <div className={styles.chartEmpty}>No low or high alarms in this {periodLabel}.</div>
        ) : null}
      </div>

      {/* Legend. */}
      <div className={styles.legend}>
        <span className={styles.legendItem}>
          <span className={styles.swatch} style={{ background: "var(--zone-warn)" }} />
          Highs (above)
        </span>
        <span className={styles.legendItem}>
          <span className={styles.swatch} style={{ background: "var(--zone-urgent)" }} />
          Lows (below)
        </span>
        <span className={styles.legendItem}>
          <span className={styles.swatchBand} />
          Overnight (12–6 AM)
        </span>
        {lowModal && buckets.lowsTotal > 0 ? (
          <span className={styles.legendItem}>{buckets.lowsTotal} lows · {buckets.highsTotal} highs</span>
        ) : null}
      </div>

      {/* Insights — descriptive only. */}
      <ul className={styles.insights}>
        {insights.map((line, i) => (
          <li key={i} className={styles.insight}>
            {line}
          </li>
        ))}
      </ul>

      {/* Facts strip. */}
      <div className={styles.facts}>
        <div className={styles.fact}>
          <span className={styles.factLabel}>Best day</span>
          <span className={styles.factValue}>
            {facts.best ? fmtDayKey(facts.best.date) : "—"}
          </span>
          <span className={styles.factSub}>
            {facts.best ? `${Math.round(facts.best.inRangePct)}% in range` : "needs full days"}
          </span>
        </div>
        <div className={styles.fact}>
          <span className={styles.factLabel}>Toughest day</span>
          <span className={styles.factValue}>
            {facts.toughest ? fmtDayKey(facts.toughest.date) : "—"}
          </span>
          <span className={styles.factSub}>
            {facts.toughest ? `${Math.round(facts.toughest.inRangePct)}% in range` : "needs full days"}
          </span>
        </div>
        <div className={styles.fact}>
          <span className={styles.factLabel}>In-target streak</span>
          <span className={styles.factValue}>
            {facts.streak} {facts.streak === 1 ? "day" : "days"}
          </span>
          <span className={styles.factSub}>≥ {STREAK_TIR_PCT}% in range</span>
        </div>
        <div className={styles.fact}>
          <span className={styles.factLabel}>CGM coverage</span>
          <span
            className={styles.factValue}
            style={coverage.low ? { color: "var(--zone-warn-text, var(--zone-warn))" } : undefined}
          >
            {coverage.pct}%
          </span>
          <span className={styles.factSub}>
            {coverage.low ? `below ${COVERAGE_LOW_PCT}%` : `~${coverage.cadenceMin} min cadence`}
          </span>
        </div>
      </div>
    </section>
  );
}
